import { useCallback, useMemo } from "react";
import {
  Table,
  Text,
  Badge,
  NumberInput,
  ActionIcon,
  Tooltip,
} from "@mantine/core";
import { IconTrash } from "@tabler/icons-react";

interface MappingRowProps {
  mappingId: string;
  practiceName: string | null;
  surgeryCenterLabName: string | null;
  physicianName: string | null;
  priority: number;
  removeLoading: boolean;
  onPriorityChange: (mappingId: string, value: number | string) => void;
  onRemove: (mappingId: string) => void;
}

export default function MappingRow({
  mappingId,
  practiceName,
  surgeryCenterLabName,
  physicianName,
  priority,
  removeLoading,
  onPriorityChange,
  onRemove,
}: MappingRowProps) {
  const target = useMemo(() => {
    if (practiceName) {
      return { name: practiceName, type: "Practice", color: "blue" };
    }
    if (surgeryCenterLabName) {
      return {
        name: surgeryCenterLabName,
        type: "Surgery Center",
        color: "teal",
      };
    }
    if (physicianName) {
      return { name: physicianName, type: "Physician", color: "violet" };
    }
    return { name: mappingId, type: "Unknown", color: "gray" };
  }, [practiceName, surgeryCenterLabName, physicianName, mappingId]);

  const handlePriorityChange = useCallback(
    (value: number | string) => onPriorityChange(mappingId, value),
    [mappingId, onPriorityChange]
  );

  const handleRemove = useCallback(
    () => onRemove(mappingId),
    [mappingId, onRemove]
  );

  return (
    <Table.Tr>
      <Table.Td>
        <Text size="sm">{target.name}</Text>
      </Table.Td>
      <Table.Td>
        <Badge color={target.color} variant="light" size="sm">
          {target.type}
        </Badge>
      </Table.Td>
      <Table.Td>
        <NumberInput
          size="xs"
          value={priority}
          min={1}
          style={{ width: 80 }}
          onChange={handlePriorityChange}
        />
      </Table.Td>
      <Table.Td>
        <Tooltip label="Remove mapping">
          <ActionIcon
            color="red"
            variant="subtle"
            onClick={handleRemove}
            loading={removeLoading}
            aria-label={`Remove mapping for ${target.name}`}
          >
            <IconTrash size={16} />
          </ActionIcon>
        </Tooltip>
      </Table.Td>
    </Table.Tr>
  );
}
